import { useEffect, useRef, useState } from 'react';
import Chart from 'chart.js';
import dayjs from 'dayjs';

import '../../../css/components/Home/ChartSection/ChartSection.css'

import { getWeekRange } from '../../../utils';

const ChartSection = ({ data }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const [ordersData, setOrdersData] = useState([]);
  const [viewMode, setViewMode] = useState('week'); // week, month, year

  const [weekOffset, setWeekOffset] = useState(0);
  const [monthOffset, setMonthOffset] = useState(0);
  const [yearOffset, setYearOffset] = useState(0);

  const [displayRange, setDisplayRange] = useState('');

  useEffect(() => {
    if (data) setOrdersData(data);
  }, [data]);

  // 計算 labels、數據、日期範圍
  const getChartData = () => {
    let labels = [];
    let shippedCounts = [];
    let notShippedCounts = [];
    let rangeText = '';

    if (viewMode === 'week') {
      const { start, end } = getWeekRange(weekOffset);
      labels = ['週一','週二','週三','週四','週五'];
      shippedCounts = Array(5).fill(0);
      notShippedCounts = Array(5).fill(0);

      ordersData.forEach(order => {
        const date = dayjs(order.deliveryDate);
        if (date.isBetween(dayjs(start).subtract(1,'day'), dayjs(end).add(1,'day'))) {
          const weekday = date.day();
          if (weekday >= 1 && weekday <= 5) {
            order.isShipped ? shippedCounts[weekday-1]++ : notShippedCounts[weekday-1]++;
          }
        }
      });

      rangeText = `${dayjs(start).format('MM/DD')} ~ ${dayjs(end).format('MM/DD')}`;
    }

    if (viewMode === 'month') {
      const current = dayjs().add(monthOffset,'month');
      const start = current.startOf('month');
      const end = current.endOf('month');
      const daysInMonth = current.daysInMonth();

      labels = Array.from({ length: daysInMonth }, (_, i) => `第${i+1}天`);
      shippedCounts = Array(daysInMonth).fill(0);
      notShippedCounts = Array(daysInMonth).fill(0);

      ordersData.forEach(order => {
        const date = dayjs(order.deliveryDate);
        if (date.isBetween(start.subtract(1,'day'), end.add(1,'day'))) {
          const idx = date.date() - 1;
          order.isShipped ? shippedCounts[idx]++ : notShippedCounts[idx]++;
        }
      });

      rangeText = `${start.format('MM/DD')} ~ ${end.format('MM/DD')}`;
    }

    if (viewMode === 'year') {
      const year = dayjs().year() + yearOffset;
      labels = ['1月','2月','3月','4月','5月','6月','7月','8月','9月','10月','11月','12月'];
      shippedCounts = Array(12).fill(0);
      notShippedCounts = Array(12).fill(0);

      ordersData.forEach(order => {
        const date = dayjs(order.deliveryDate);
        if (date.year() === year) {
          order.isShipped ? shippedCounts[date.month()]++ : notShippedCounts[date.month()]++;
        }
      });

      rangeText = `${year}/01/01 ~ ${year}/12/31`;
    }

    return { labels, shippedCounts, notShippedCounts, rangeText };
  };

  useEffect(() => {
    if (!ordersData.length) return;

    const { labels, shippedCounts, notShippedCounts, rangeText } = getChartData();

    setDisplayRange(rangeText);

    const ctx = canvasRef.current.getContext('2d');
    if(chartRef.current) chartRef.current.destroy();

    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          { label:'已送達', data: shippedCounts, backgroundColor:'rgba(2,117,216,0.7)' },
          { label:'未送達', data: notShippedCounts, backgroundColor:'rgba(255,99,132,0.7)' }
        ]
      },
      options:{
        responsive:true,
        plugins:{legend:{position:'top'}, tooltip:{mode:'index',intersect:false}},
        interaction:{mode:'index', intersect:false},
        scales:{x:{stacked:true}, y:{stacked:true, beginAtZero:true}}
      }
    });
  }, [ordersData, viewMode, weekOffset, monthOffset, yearOffset]);

  const renderOffsetButtons = () => {
    if (viewMode === 'week') return (
      <div style={{marginBottom:'10px'}}>
        <button onClick={()=>setWeekOffset(weekOffset-1)}>上週</button>
        <button onClick={()=>setWeekOffset(0)}>本週</button>
        <button onClick={()=>setWeekOffset(weekOffset+1)}>下週</button>
      </div>
    );
    if (viewMode === 'month') return (
      <div style={{marginBottom:'10px'}}>
        <button onClick={()=>setMonthOffset(monthOffset-1)}>上月</button>
        <button onClick={()=>setMonthOffset(0)}>本月</button>
        <button onClick={()=>setMonthOffset(monthOffset+1)}>下月</button>
      </div>
    );
    if (viewMode === 'year') return (
      <div style={{marginBottom:'10px'}}>
        <button onClick={()=>setYearOffset(yearOffset-1)}>去年</button>
        <button onClick={()=>setYearOffset(0)}>今年</button>
        <button onClick={()=>setYearOffset(yearOffset+1)}>明年</button>
      </div>
    );
  };

  return (
    <>
      <div className="card-header"><i className="fas fa-chart-bar me-1"></i> 訂單統計</div>
      <div className="card-body chart-body">
        <div style={{marginBottom:'10px'}}>
          <button onClick={()=>setViewMode('week')}>週</button>
          <button onClick={()=>setViewMode('month')}>月</button>
          <button onClick={()=>setViewMode('year')}>年</button>
        </div>

        {renderOffsetButtons()}

        <canvas ref={canvasRef}></canvas>
      </div>

      <div className="card-footer small text-muted">
        {displayRange && `日期範圍: ${displayRange}`}
      </div>
    </>
  );
};

export { ChartSection };
